import { useMemo } from 'react';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { type ChildTreeNode } from '@/lib/child-node';

/**
 * Sort and filter the browser view's leading-section children client-side (DM-2517).
 *
 * Sort and filter are server-side *asset*-query parameters; the tree endpoint has neither, so
 * the same three values the gallery already holds get applied to the children here instead.
 *
 * Sort mapping:
 * - CREATED_AT → the node's own `created_at`, with children that have none sorted last in both
 *   directions. Previously a single child missing `created_at` switched the *whole* list to name
 *   order while the sort control still read "Created" — an invisible failure. Grouping dated
 *   before undated keeps the comparator total and transitive without that all-or-nothing check,
 *   and without the two non-null assertions it used to need.
 * - NAME, and every other value (MANUAL, TRASHED_AT, TYPE, COUNT) → case-insensitive name.
 *   The fallback is deliberate: a sort with no meaning for a node must not throw and must not drop
 *   children. There is no UPDATED_AT sort value in the app, so nothing maps to `updated_at`.
 *
 * DESC negates the comparator rather than reversing the sorted array. `Array.prototype.sort` is
 * stable, so reversing also reversed *equal* elements — two children with the same name or
 * timestamp swapped places between ASC and DESC instead of holding their order.
 *
 * Filtering is a case-insensitive name match only — the asset filter's other facets have no
 * node equivalent, and pretending otherwise would hide folders for invisible reasons.
 */
export const useSortedFilteredChildren = (
  children: Array<ChildTreeNode> | undefined,
  {
    sortValue,
    sortOrder,
    queryString,
  }: { sortValue?: SortOrderValues; sortOrder?: SortOrderOptions; queryString?: string },
) => {
  return useMemo(() => {
    const all = children ?? [];
    const trimmedQuery = queryString?.trim().toLowerCase();
    const filtered = trimmedQuery ? all.filter((child) => child.node.name.toLowerCase().includes(trimmedQuery)) : all;

    const direction = sortOrder === SortOrderOptions.DESC ? -1 : 1;
    const byName = (a: ChildTreeNode, b: ChildTreeNode) =>
      a.node.name.localeCompare(b.node.name, undefined, { sensitivity: 'base' });

    const compare = (a: ChildTreeNode, b: ChildTreeNode) => {
      if (sortValue !== SortOrderValues.CREATED_AT) {
        return direction * byName(a, b);
      }

      const aCreatedAt = a.node.created_at;
      const bCreatedAt = b.node.created_at;

      if (aCreatedAt && bCreatedAt) {
        // Scalar comparison, not localeCompare: these are fixed-format timestamps, and ICU collation
        // is a text rule that happens to agree rather than the ordering actually being asked for.
        // localeCompare stays below for names, where locale-aware collation is the point.
        if (aCreatedAt === bCreatedAt) {
          return direction * byName(a, b);
        }
        return direction * (aCreatedAt < bCreatedAt ? -1 : 1);
      }
      // Undated last whichever way the dated ones are running, so the group boundary is not
      // multiplied by `direction`.
      if (aCreatedAt) {
        return -1;
      }
      if (bCreatedAt) {
        return 1;
      }
      return direction * byName(a, b);
    };

    return [...filtered].sort(compare);
  }, [children, sortValue, sortOrder, queryString]);
};
